 import { useEffect, useRef } from 'react';
 import { gsap } from 'gsap';
 import { ScrollTrigger } from 'gsap/ScrollTrigger';
 
 // Register GSAP plugins
 if (typeof window !== 'undefined') {
   gsap.registerPlugin(ScrollTrigger);
 }
 
 // Check for reduced motion preference
 export const prefersReducedMotion = (): boolean => {
   if (typeof window === 'undefined') return false;
   return window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.innerWidth < 768;
 };
 
 // Custom hook for GSAP animations with reduced motion support
 export const useGsapAnimation = <T extends HTMLElement>() => {
   const ref = useRef<T>(null);
   const reducedMotion = prefersReducedMotion();
 
   return { ref, reducedMotion };
 };
 
 // Fade in animation
 export const fadeIn = (
   element: HTMLElement | null,
   options?: { delay?: number; duration?: number; y?: number }
 ) => {
   if (!element || prefersReducedMotion()) {
     if (element) gsap.set(element, { opacity: 1 });
     return;
   }
 
   const { delay = 0, duration = 0.6, y = 30 } = options || {};
 
   gsap.fromTo(
     element,
     { opacity: 0, y },
     { opacity: 1, y: 0, duration, delay, ease: 'power3.out' }
   );
 };
 
 // Stagger animation for multiple elements
 export const staggerFadeIn = (
   elements: HTMLElement[] | NodeListOf<Element> | null,
   options?: { delay?: number; stagger?: number; y?: number }
 ) => {
   if (!elements || prefersReducedMotion()) {
     if (elements) gsap.set(elements, { opacity: 1 });
     return;
   }
 
   const { delay = 0, stagger = 0.1, y = 30 } = options || {};
 
   gsap.fromTo(
     elements,
     { opacity: 0, y },
     { opacity: 1, y: 0, duration: 0.6, delay, stagger, ease: 'power3.out' }
   );
 };
 
 // Scroll trigger animation
 export const scrollReveal = (
   trigger: HTMLElement | null,
   options?: { y?: number; start?: string }
 ) => {
   if (!trigger || prefersReducedMotion()) {
     if (trigger) gsap.set(trigger, { opacity: 1 });
     return;
   }
 
   const { y = 40, start = 'top 85%' } = options || {};
 
   gsap.fromTo(
     trigger,
     { opacity: 0, y },
     {
       opacity: 1,
       y: 0,
       duration: 0.8,
       ease: 'power3.out',
       scrollTrigger: {
         trigger,
         start,
         toggleActions: 'play none none reverse',
       },
     }
   );
 };
 
 // Timeline progress animation
 export const timelineProgress = (
   progressBar: HTMLElement | null,
   container: HTMLElement | null
 ) => {
   if (!progressBar || !container || prefersReducedMotion()) {
     if (progressBar) gsap.set(progressBar, { scaleY: 1 });
     return;
   }
 
   gsap.fromTo(
     progressBar,
     { scaleY: 0 },
     {
       scaleY: 1,
       ease: 'none',
       scrollTrigger: {
         trigger: container,
         start: 'top center',
         end: 'bottom center',
         scrub: 1,
       },
     }
   );
 };
 
 // Hero text animation
 export const heroTextAnimation = (container: HTMLElement | null) => {
   if (!container || prefersReducedMotion()) {
     if (container) {
       const elements = container.querySelectorAll('.hero-animate');
       gsap.set(elements, { opacity: 1 });
     }
     return;
   }
 
   const tl = gsap.timeline({ delay: 0.2 });
   const words = container.querySelectorAll('.hero-word');
   const subtitle = container.querySelector('.hero-subtitle');
   const ctas = container.querySelectorAll('.hero-cta');
 
   // Animate words
   if (words.length > 0) {
     tl.fromTo(
       words,
       { opacity: 0, y: 50, rotateX: -20 },
       { opacity: 1, y: 0, rotateX: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out' }
     );
   }
 
   // Animate subtitle
   if (subtitle) {
     tl.fromTo(
       subtitle,
       { opacity: 0, y: 20 },
       { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' },
       '-=0.3'
     );
   }
 
   // Animate CTAs
   if (ctas.length > 0) {
     tl.fromTo(
       ctas,
       { opacity: 0, y: 20, scale: 0.9 },
       { opacity: 1, y: 0, scale: 1, duration: 0.5, stagger: 0.1, ease: 'back.out(1.7)' },
       '-=0.2'
     );
   }
 
   return tl;
 };
 
 export { gsap, ScrollTrigger };